import { LiveTrainingCardProps, TrainingStatus } from 'src/components/LiveTrainingCard';
import { ISODateTimeString } from 'src/utils/date';

export type CalendarLiveTrainingSession = LiveTrainingCardProps & {
  courseId: string;
  instanceId: string;
  courseMaterialsUrl: string;
};

interface EventTemplate {
  id: string;
  title: string;
  subtitle: string;
  products: string[];
  host: string;
  isFree: boolean;
  isSaved: boolean;
  enrolled: boolean;
  dayOfMonth: number;
  startHour: number;
  durationHours: number;
}

// Template data for events (3 per month for 12 months)
const eventTemplates: EventTemplate[] = [
  // Month 1
  {
    id: '1-1',
    title: 'Jira Service Management Fundamentals',
    subtitle: 'Master the basics of Jira Service Management',
    products: ['Jira Service Management'],
    host: 'ServiceNow Experts',
    isFree: false,
    isSaved: true,
    enrolled: true,
    dayOfMonth: 5,
    startHour: 14,
    durationHours: 4,
  },
  {
    id: '1-2',
    title: 'Confluence for Documentation Teams',
    subtitle: 'Create and maintain effective documentation',
    products: ['Confluence'],
    host: 'Documentation Pros',
    isFree: true,
    isSaved: false,
    enrolled: false,
    dayOfMonth: 12,
    startHour: 15,
    durationHours: 4,
  },
  {
    id: '1-3',
    title: 'Bitbucket Pipelines Deep Dive',
    subtitle: 'Advanced CI/CD with Bitbucket',
    products: ['Bitbucket'],
    host: 'DevOps Academy',
    isFree: false,
    isSaved: false,
    enrolled: true,
    dayOfMonth: 18,
    startHour: 13,
    durationHours: 4,
  },

  // Month 2
  {
    id: '2-1',
    title: 'Jira Align for Enterprise Planning',
    subtitle: 'Scale agile across your organization',
    products: ['Jira Align'],
    host: 'Enterprise Agile Coaches',
    isFree: false,
    isSaved: true,
    enrolled: true,
    dayOfMonth: 8,
    startHour: 14,
    durationHours: 4,
  },
  {
    id: '2-2',
    title: 'Compass for Developer Experience',
    subtitle: 'Improve developer productivity with Compass',
    products: ['Compass'],
    host: 'Platform Engineering Team',
    isFree: true,
    isSaved: false,
    enrolled: false,
    dayOfMonth: 15,
    startHour: 16,
    durationHours: 4,
  },
  {
    id: '2-3',
    title: 'Trello for Project Management',
    subtitle: 'Visual project management made easy',
    products: ['Trello'],
    host: 'Productivity Experts',
    isFree: false,
    isSaved: false,
    enrolled: true,
    dayOfMonth: 22,
    startHour: 14,
    durationHours: 3,
  },

  // Month 3
  {
    id: '3-1',
    title: 'Loom for Async Communication',
    subtitle: 'Master video messaging for remote teams',
    products: ['Loom'],
    host: 'Remote Work Specialists',
    isFree: true,
    isSaved: true,
    enrolled: true,
    dayOfMonth: 5,
    startHour: 15,
    durationHours: 3,
  },
  {
    id: '3-2',
    title: 'Rovo AI Assistant Training',
    subtitle: 'Leverage AI to boost team productivity',
    products: ['Rovo'],
    host: 'AI Innovation Lab',
    isFree: false,
    isSaved: false,
    enrolled: false,
    dayOfMonth: 12,
    startHour: 14,
    durationHours: 4,
  },
  {
    id: '3-3',
    title: 'Jira Product Discovery Workshop',
    subtitle: 'Prioritize and validate product ideas',
    products: ['Jira Product Discovery'],
    host: 'Product Management Guild',
    isFree: false,
    isSaved: false,
    enrolled: true,
    dayOfMonth: 19,
    startHour: 16,
    durationHours: 4,
  },

  // Month 4
  {
    id: '4-1',
    title: 'Guard Security Essentials',
    subtitle: 'Protect your Atlassian environment',
    products: ['Guard'],
    host: 'Security Operations Team',
    isFree: false,
    isSaved: true,
    enrolled: true,
    dayOfMonth: 6,
    startHour: 14,
    durationHours: 4,
  },
  {
    id: '4-2',
    title: 'Confluence and Jira Integration',
    subtitle: 'Connect documentation with work tracking',
    products: ['Confluence', 'Jira'],
    host: 'Integration Specialists',
    isFree: true,
    isSaved: false,
    enrolled: false,
    dayOfMonth: 13,
    startHour: 15,
    durationHours: 4,
  },
  {
    id: '4-3',
    title: 'Bitbucket Code Review Best Practices',
    subtitle: 'Improve code quality through effective reviews',
    products: ['Bitbucket'],
    host: 'Code Quality Team',
    isFree: false,
    isSaved: false,
    enrolled: true,
    dayOfMonth: 20,
    startHour: 14,
    durationHours: 3,
  },

  // Month 5
  {
    id: '5-1',
    title: 'Jira Automation Masterclass',
    subtitle: 'Automate repetitive tasks and workflows',
    products: ['Jira'],
    host: 'Automation Experts',
    isFree: false,
    isSaved: true,
    enrolled: true,
    dayOfMonth: 3,
    startHour: 14,
    durationHours: 4,
  },
  {
    id: '5-2',
    title: 'Trello Power-Ups and Integrations',
    subtitle: 'Extend Trello with powerful add-ons',
    products: ['Trello'],
    host: 'Trello Champions',
    isFree: true,
    isSaved: false,
    enrolled: false,
    dayOfMonth: 10,
    startHour: 16,
    durationHours: 3,
  },
  {
    id: '5-3',
    title: 'Compass Service Catalog Management',
    subtitle: 'Build and maintain your service catalog',
    products: ['Compass'],
    host: 'Platform Team',
    isFree: false,
    isSaved: false,
    enrolled: true,
    dayOfMonth: 17,
    startHour: 15,
    durationHours: 4,
  },

  // Month 6
  {
    id: '6-1',
    title: 'Jira Service Management for ITSM',
    subtitle: 'Implement ITIL best practices',
    products: ['Jira Service Management'],
    host: 'ITSM Consultants',
    isFree: false,
    isSaved: true,
    enrolled: true,
    dayOfMonth: 8,
    startHour: 14,
    durationHours: 4,
  },
  {
    id: '6-2',
    title: 'Confluence Templates and Blueprints',
    subtitle: 'Standardize documentation with templates',
    products: ['Confluence'],
    host: 'Content Strategy Team',
    isFree: true,
    isSaved: false,
    enrolled: false,
    dayOfMonth: 15,
    startHour: 15,
    durationHours: 3,
  },
  {
    id: '6-3',
    title: 'Loom for Sales and Marketing',
    subtitle: 'Create engaging video content',
    products: ['Loom'],
    host: 'Marketing Innovators',
    isFree: false,
    isSaved: false,
    enrolled: true,
    dayOfMonth: 22,
    startHour: 16,
    durationHours: 3,
  },

  // Month 7
  {
    id: '7-1',
    title: 'Rovo Knowledge Management',
    subtitle: 'Centralize and discover team knowledge',
    products: ['Rovo'],
    host: 'Knowledge Management Team',
    isFree: false,
    isSaved: true,
    enrolled: true,
    dayOfMonth: 5,
    startHour: 14,
    durationHours: 4,
  },
  {
    id: '7-2',
    title: 'Jira Align Portfolio Planning',
    subtitle: 'Strategic planning at scale',
    products: ['Jira Align'],
    host: 'Enterprise Agile Team',
    isFree: true,
    isSaved: false,
    enrolled: false,
    dayOfMonth: 12,
    startHour: 15,
    durationHours: 4,
  },
  {
    id: '7-3',
    title: 'Bitbucket Branch Permissions',
    subtitle: 'Secure your code repositories',
    products: ['Bitbucket'],
    host: 'Security Team',
    isFree: false,
    isSaved: false,
    enrolled: true,
    dayOfMonth: 19,
    startHour: 14,
    durationHours: 3,
  },

  // Month 8
  {
    id: '8-1',
    title: 'Jira Product Discovery for PMs',
    subtitle: 'Product discovery and validation',
    products: ['Jira Product Discovery'],
    host: 'Product Leadership',
    isFree: false,
    isSaved: true,
    enrolled: true,
    dayOfMonth: 3,
    startHour: 14,
    durationHours: 4,
  },
  {
    id: '8-2',
    title: 'Guard Data Residency and Compliance',
    subtitle: 'Meet regulatory requirements',
    products: ['Guard'],
    host: 'Compliance Experts',
    isFree: true,
    isSaved: false,
    enrolled: false,
    dayOfMonth: 10,
    startHour: 16,
    durationHours: 4,
  },
  {
    id: '8-3',
    title: 'Jira for Agile Teams',
    subtitle: 'Scrum and Kanban best practices',
    products: ['Jira'],
    host: 'Agile Coaches',
    isFree: false,
    isSaved: false,
    enrolled: true,
    dayOfMonth: 17,
    startHour: 15,
    durationHours: 4,
  },

  // Month 9
  {
    id: '9-1',
    title: 'Compass Scorecards and Standards',
    subtitle: 'Define and track engineering standards',
    products: ['Compass'],
    host: 'Engineering Excellence',
    isFree: false,
    isSaved: true,
    enrolled: true,
    dayOfMonth: 7,
    startHour: 14,
    durationHours: 4,
  },
  {
    id: '9-2',
    title: 'Trello for Remote Teams',
    subtitle: 'Collaborate effectively from anywhere',
    products: ['Trello'],
    host: 'Remote Work Experts',
    isFree: true,
    isSaved: false,
    enrolled: false,
    dayOfMonth: 14,
    startHour: 15,
    durationHours: 3,
  },
  {
    id: '9-3',
    title: 'Confluence Advanced Macros',
    subtitle: 'Create dynamic and interactive pages',
    products: ['Confluence'],
    host: 'Content Architects',
    isFree: false,
    isSaved: false,
    enrolled: true,
    dayOfMonth: 21,
    startHour: 16,
    durationHours: 4,
  },

  // Month 10
  {
    id: '10-1',
    title: 'Loom for Customer Success',
    subtitle: 'Deliver exceptional customer experiences',
    products: ['Loom'],
    host: 'Customer Success Team',
    isFree: false,
    isSaved: true,
    enrolled: true,
    dayOfMonth: 4,
    startHour: 14,
    durationHours: 3,
  },
  {
    id: '10-2',
    title: 'Rovo AI Search and Discovery',
    subtitle: 'Find information faster with AI',
    products: ['Rovo'],
    host: 'AI Research Team',
    isFree: true,
    isSaved: false,
    enrolled: false,
    dayOfMonth: 11,
    startHour: 15,
    durationHours: 4,
  },
  {
    id: '10-3',
    title: 'Jira Service Management Assets',
    subtitle: 'Track and manage IT assets',
    products: ['Jira Service Management'],
    host: 'Asset Management Team',
    isFree: false,
    isSaved: false,
    enrolled: true,
    dayOfMonth: 18,
    startHour: 14,
    durationHours: 4,
  },

  // Month 11
  {
    id: '11-1',
    title: 'Bitbucket Merge Strategies',
    subtitle: 'Master Git workflows and merging',
    products: ['Bitbucket'],
    host: 'Git Masters',
    isFree: false,
    isSaved: true,
    enrolled: true,
    dayOfMonth: 2,
    startHour: 14,
    durationHours: 4,
  },
  {
    id: '11-2',
    title: 'Jira Align OKRs and Goals',
    subtitle: 'Align teams with organizational objectives',
    products: ['Jira Align'],
    host: 'Strategy Team',
    isFree: true,
    isSaved: false,
    enrolled: false,
    dayOfMonth: 9,
    startHour: 16,
    durationHours: 4,
  },
  {
    id: '11-3',
    title: 'Guard Access Controls',
    subtitle: 'Implement fine-grained permissions',
    products: ['Guard'],
    host: 'Security Architecture',
    isFree: false,
    isSaved: false,
    enrolled: true,
    dayOfMonth: 16,
    startHour: 15,
    durationHours: 4,
  },

  // Month 12
  {
    id: '12-1',
    title: 'Jira Product Discovery Roadmapping',
    subtitle: 'Build data-driven product roadmaps',
    products: ['Jira Product Discovery'],
    host: 'Product Strategy',
    isFree: false,
    isSaved: true,
    enrolled: true,
    dayOfMonth: 6,
    startHour: 14,
    durationHours: 4,
  },
  {
    id: '12-2',
    title: 'Compass for Microservices',
    subtitle: 'Manage complex service architectures',
    products: ['Compass'],
    host: 'Architecture Team',
    isFree: true,
    isSaved: false,
    enrolled: false,
    dayOfMonth: 13,
    startHour: 15,
    durationHours: 4,
  },
  {
    id: '12-3',
    title: 'Jira and Confluence for DevOps',
    subtitle: 'Integrate development and operations',
    products: ['Jira', 'Confluence'],
    host: 'DevOps Guild',
    isFree: false,
    isSaved: false,
    enrolled: true,
    dayOfMonth: 20,
    startHour: 16,
    durationHours: 4,
  },
];

/**
 * Generates calendar events programmatically based on templates
 * Events are always in the future, starting from next month to ensure they never fall in the past
 */
const generateCalendarEvents = (): CalendarLiveTrainingSession[] => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  // Start from next month to ensure all events are in the future
  const startMonth = currentMonth + 1;

  return eventTemplates.map((template, index) => {
    const monthOffset = Math.floor(index / 3);
    const targetMonth = (startMonth + monthOffset) % 12;
    const targetYear = currentYear + Math.floor((startMonth + monthOffset) / 12);

    const startDate = new Date(Date.UTC(targetYear, targetMonth, template.dayOfMonth, template.startHour, 0, 0));
    const endDate = new Date(startDate);

    endDate.setUTCHours(startDate.getUTCHours() + template.durationHours);

    return {
      courseId: `event-${template.id}`,
      instanceId: `session-${template.id}`,
      title: template.title,
      subtitle: template.subtitle,
      startDateTime: startDate.toISOString() as ISODateTimeString,
      endDateTime: endDate.toISOString() as ISODateTimeString,
      status: 'upcoming' as TrainingStatus,
      enrolled: template.enrolled,
      products: template.products,
      host: template.host,
      isFree: template.isFree,
      isSaved: template.isSaved,
      isPrivate: false,
      courseMaterialsUrl: '',
      videoCallUrl: 'https://example.com/videocall',
    };
  });
};

export const mockCalendarEvents: CalendarLiveTrainingSession[] = generateCalendarEvents();
